import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Zap, Calculator, ArrowRight, ArrowLeft, Scale, Ruler, User, Activity, Target, Flame, Globe, UserCircle, Crown, Shield, Gauge, HeartPulse, Plus, X, MessageCircle, Briefcase } from 'lucide-react';
import StatTile from '../components/ui/StatTile';
import { toast } from 'sonner';
import { registerServiceWorker, subscribeToPush } from '../utils/pushNotifications';
import { Progress } from '../components/ui/progress';
import { PROFILE_DEFINITIONS } from '../utils/themeManager';
import ProfileTransition from '../components/ProfileTransition';
import DietaryBriefingModal from '../components/DietaryBriefingModal';
import OnboardingPathChoice from '../components/OnboardingPathChoice';
import OnboardingBypassForm from '../components/OnboardingBypassForm';
import { METABOLIC_CONDITIONS, ALLERGIES_OPTIONS } from '../components/settings/SettingsHealthProfile';
import { COMMUNICATION_STYLES } from '../components/settings/SettingsCommunicationStyle';
import { 
  UNIT_SYSTEMS, 
  kgToLbs, 
  lbsToKg, 
  cmToFeetInches, 
  feetInchesToCm,
  getDefaultUnitSystem 
} from '../utils/unitConversions';

// ============ TUNNEL DE TRI CONFIG ============

const PROFILE_GROUPS = [
  { id: 'femme', icon: '👩', i18nKey: 'tunnel_pg_femme', defaultFr: 'Femme', color: 'from-pink-500/20 to-fuchsia-500/20', border: 'border-pink-500', text: 'text-pink-400' },
  { id: 'homme', icon: '👨', i18nKey: 'tunnel_pg_homme', defaultFr: 'Homme', color: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-500', text: 'text-blue-400' },
  { id: 'senior', icon: '🧓', i18nKey: 'tunnel_pg_senior', defaultFr: 'Senior (65+)', color: 'from-amber-500/20 to-yellow-500/20', border: 'border-amber-500', text: 'text-amber-400' },
];

const MISSIONS = [
  { id: 'perte_poids', icon: '🔥', i18nKey: 'tunnel_m_perte', defaultFr: 'Perte de poids', descKey: 'tunnel_m_perte_desc', defaultDescFr: 'Brûler les graisses intelligemment', color: 'from-red-500/20 to-orange-500/20', border: 'border-red-500', text: 'text-red-400' },
  { id: 'performance', icon: '⚡', i18nKey: 'tunnel_m_perf', defaultFr: 'Performance Élite', descKey: 'tunnel_m_perf_desc', defaultDescFr: 'Maximiser la force et la masse', color: 'from-orange-500/20 to-amber-500/20', border: 'border-orange-500', text: 'text-orange-400' },
  { id: 'mobilite', icon: '🧘', i18nKey: 'tunnel_m_mob', defaultFr: 'Mobilité & Santé', descKey: 'tunnel_m_mob_desc', defaultDescFr: 'Équilibre, souplesse et bien-être', color: 'from-teal-500/20 to-cyan-500/20', border: 'border-teal-500', text: 'text-teal-400' },
];

const TECH_LEVELS = [
  { id: 'debutant', icon: '🧭', i18nKey: 'tunnel_tl_deb', defaultFr: 'Débutant', descKey: 'tunnel_tl_deb_desc', defaultDescFr: 'Guide-moi pas à pas', color: 'from-violet-500/20 to-purple-500/20', border: 'border-violet-500', text: 'text-violet-400' },
  { id: 'expert', icon: '🎯', i18nKey: 'tunnel_tl_exp', defaultFr: 'Expert', descKey: 'tunnel_tl_exp_desc', defaultDescFr: 'Laisse-moi gérer', color: 'from-emerald-500/20 to-green-500/20', border: 'border-emerald-500', text: 'text-emerald-400' },
];

const DIETARY_APPROACHES = [
  { id: 'standard', i18nKey: 'tunnel_da_standard', defaultFr: 'Standard (Flexible)', color: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-500', text: 'text-blue-400' },
  { id: 'keto', i18nKey: 'tunnel_da_keto', defaultFr: 'Cétogène (Keto)', color: 'from-purple-500/20 to-violet-500/20', border: 'border-purple-500', text: 'text-purple-400' },
  { id: 'paleo', i18nKey: 'tunnel_da_paleo', defaultFr: 'Paléo', color: 'from-amber-500/20 to-yellow-500/20', border: 'border-amber-500', text: 'text-amber-400' },
  { id: 'carnivore', i18nKey: 'tunnel_da_carnivore', defaultFr: 'Carnivore', color: 'from-red-500/20 to-rose-500/20', border: 'border-red-500', text: 'text-red-400' },
  { id: 'vegetarian', i18nKey: 'tunnel_da_vegetarian', defaultFr: 'Végétarien', color: 'from-green-500/20 to-lime-500/20', border: 'border-green-500', text: 'text-green-400' },
  { id: 'vegan', i18nKey: 'tunnel_da_vegan', defaultFr: 'Végétalien (Vegan)', color: 'from-emerald-500/20 to-teal-500/20', border: 'border-emerald-500', text: 'text-emerald-400' },
  { id: 'pescatarian', i18nKey: 'tunnel_da_pesca', defaultFr: 'Pescétarien', color: 'from-cyan-500/20 to-sky-500/20', border: 'border-cyan-500', text: 'text-cyan-400' },
  { id: 'high_protein', i18nKey: 'tunnel_da_hp', defaultFr: 'Haute Protéine', color: 'from-orange-500/20 to-amber-500/20', border: 'border-orange-500', text: 'text-orange-400' },
];

// ============ EXISTING CONFIG ============

const ACTIVITY_LEVELS = {
  fr: [
    { value: 'sedentary', label: 'Sédentaire', desc: 'Peu ou pas d\'exercice', factor: 1.2 },
    { value: 'light', label: 'Légèrement actif', desc: 'Exercice léger 1-3 jours/sem', factor: 1.375 },
    { value: 'moderate', label: 'Modérément actif', desc: 'Exercice modéré 3-5 jours/sem', factor: 1.55 },
    { value: 'active', label: 'Très actif', desc: 'Exercice intense 6-7 jours/sem', factor: 1.725 },
    { value: 'athlete', label: 'Athlète', desc: 'Entraînement intense quotidien', factor: 1.9 },
  ],
  en: [
    { value: 'sedentary', label: 'Sedentary', desc: 'Little or no exercise', factor: 1.2 },
    { value: 'light', label: 'Lightly active', desc: 'Light exercise 1-3 days/week', factor: 1.375 },
    { value: 'moderate', label: 'Moderately active', desc: 'Moderate exercise 3-5 days/week', factor: 1.55 },
    { value: 'active', label: 'Very active', desc: 'Hard exercise 6-7 days/week', factor: 1.725 },
    { value: 'athlete', label: 'Athlete', desc: 'Intense daily training', factor: 1.9 },
  ],
  es: [
    { value: 'sedentary', label: 'Sedentario', desc: 'Poco o ningún ejercicio', factor: 1.2 },
    { value: 'light', label: 'Ligeramente activo', desc: 'Ejercicio ligero 1-3 días/sem', factor: 1.375 },
    { value: 'moderate', label: 'Moderadamente activo', desc: 'Ejercicio moderado 3-5 días/sem', factor: 1.55 },
    { value: 'active', label: 'Muy activo', desc: 'Ejercicio intenso 6-7 días/sem', factor: 1.725 },
    { value: 'athlete', label: 'Atleta', desc: 'Entrenamiento intenso diario', factor: 1.9 },
  ],
};

const GOALS = {
  fr: [
    { value: 'lose', label: 'Perdre du poids', calorieAdjust: -500 },
    { value: 'maintain', label: 'Maintenir mon poids', calorieAdjust: 0 },
    { value: 'gain', label: 'Prendre du muscle', calorieAdjust: 300 },
  ],
  en: [
    { value: 'lose', label: 'Lose weight', calorieAdjust: -500 },
    { value: 'maintain', label: 'Maintain weight', calorieAdjust: 0 },
    { value: 'gain', label: 'Build muscle', calorieAdjust: 300 },
  ],
  es: [
    { value: 'lose', label: 'Perder peso', calorieAdjust: -500 },
    { value: 'maintain', label: 'Mantener mi peso', calorieAdjust: 0 },
    { value: 'gain', label: 'Ganar músculo', calorieAdjust: 300 },
  ],
};

// 🛠️ NEAT — Professional activity factor (Iter. 385).
// Multiplicateur appliqué au TDEE pour corriger le budget calorique des
// travailleurs debout/physiques (chef, infirmière, construction…). Sans cette
// variable, un chef en cuisine 84h/sem se retrouve en mode famine à 1654 kcal.
const JOB_PROFILES = {
  fr: [
    { value: 'desk',     label: 'Sédentaire',        desc: 'Bureau, assis (cadre, programmeur, comptable)',           factor: 1.00 },
    { value: 'light',    label: 'Actif léger',       desc: 'Debout, marche légère (enseignant, coiffeur, vendeur)',   factor: 1.15 },
    { value: 'moderate', label: 'Physique modéré',   desc: 'Mouvement constant + charges (chef, infirmier, server)',  factor: 1.30 },
    { value: 'intense',  label: 'Physique intense',  desc: 'Travail lourd (construction, manutention, débardeur)',    factor: 1.45 },
  ],
  en: [
    { value: 'desk',     label: 'Desk job',          desc: 'Seated all day (office, dev, accounting)',                factor: 1.00 },
    { value: 'light',    label: 'Lightly active',    desc: 'Standing + walking (teacher, hair stylist, retail)',      factor: 1.15 },
    { value: 'moderate', label: 'Moderately physical', desc: 'Constant motion + loads (chef, nurse, server)',         factor: 1.30 },
    { value: 'intense',  label: 'Physically intense', desc: 'Heavy work (construction, warehouse, logistics)',        factor: 1.45 },
  ],
  es: [
    { value: 'desk',     label: 'Sedentario',        desc: 'Oficina, sentado (programador, contable)',                factor: 1.00 },
    { value: 'light',    label: 'Activo ligero',     desc: 'De pie, caminata ligera (profesor, peluquero, ventas)',   factor: 1.15 },
    { value: 'moderate', label: 'Físico moderado',   desc: 'Movimiento constante + cargas (chef, enfermería)',        factor: 1.30 },
    { value: 'intense',  label: 'Físico intenso',    desc: 'Trabajo pesado (construcción, manipulación)',             factor: 1.45 },
  ],
};

const IMC_CATEGORIES = {
  fr: [
    { max: 18.5, label: 'Insuffisance ponderale', color: 'text-blue-400', bg: 'bg-blue-500/20' },
    { max: 24.9, label: 'Poids normal', color: 'text-green-400', bg: 'bg-green-500/20' },
    { max: 29.9, label: 'Surpoids', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
    { max: 34.9, label: 'Obesite classe 1', color: 'text-orange-400', bg: 'bg-orange-500/20' },
    { max: Infinity, label: 'Obesite severe', color: 'text-red-600', bg: 'bg-red-600/20' },
  ],
  en: [
    { max: 18.5, label: 'Underweight', color: 'text-blue-400', bg: 'bg-blue-500/20' },
    { max: 24.9, label: 'Normal weight', color: 'text-green-400', bg: 'bg-green-500/20' },
    { max: 29.9, label: 'Overweight', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
    { max: 34.9, label: 'Obesity class 1', color: 'text-orange-400', bg: 'bg-orange-500/20' },
    { max: Infinity, label: 'Severe obesity', color: 'text-red-600', bg: 'bg-red-600/20' },
  ],
  es: [
    { max: 18.5, label: 'Bajo peso', color: 'text-blue-400', bg: 'bg-blue-500/20' },
    { max: 24.9, label: 'Peso normal', color: 'text-green-400', bg: 'bg-green-500/20' },
    { max: 29.9, label: 'Sobrepeso', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
    { max: 34.9, label: 'Obesidad clase 1', color: 'text-orange-400', bg: 'bg-orange-500/20' },
    { max: Infinity, label: 'Obesidad severa', color: 'text-red-600', bg: 'bg-red-600/20' },
  ],
};

const TOTAL_STEPS = 9; // 0: Tunnel, 1: Basic, 2: Activity, 3: Deficit Speed, 4: Macros, 5: Health, 6: Comm Style, 7: Cultural Quiz, 8: Profile

// ============ DEFICIT SPEED OPTIONS ============
const DEFICIT_SPEEDS = [
  { key: '0.5', weekly: '0.5 lb', deficit: 250, labelKey: 'deficit_serein_label', defaultLabelFr: 'Serein', descKey: 'deficit_serein_desc', defaultDescFr: 'Ideal pour la santé a long terme.', color: 'from-green-500 to-green-400', border: 'border-green-500', text: 'text-green-400', bg: 'bg-green-500/10', barWidth: '25%' },
  { key: '1.0', weekly: '1.0 lb', deficit: 500, labelKey: 'deficit_det_label', defaultLabelFr: 'Determine', descKey: 'deficit_det_desc', defaultDescFr: 'Equilibre parfait effort/resultat.', color: 'from-yellow-500 to-amber-400', border: 'border-yellow-500', text: 'text-yellow-400', bg: 'bg-yellow-500/10', barWidth: '50%' },
  { key: '1.5', weekly: '1.5 lb', deficit: 750, labelKey: 'deficit_intense_label', defaultLabelFr: 'Intense', descKey: 'deficit_intense_desc', defaultDescFr: 'Necessite une discipline rigoureuse.', color: 'from-orange-500 to-orange-400', border: 'border-orange-500', text: 'text-orange-400', bg: 'bg-orange-500/10', barWidth: '75%' },
  { key: '2.0', weekly: '2.0 lb', deficit: 1000, labelKey: 'deficit_commando_label', defaultLabelFr: 'Commando', descKey: 'deficit_commando_desc', defaultDescFr: 'Reserve aux objectifs a court terme.', color: 'from-red-600 to-red-500', border: 'border-red-500', text: 'text-red-400', bg: 'bg-red-500/10', barWidth: '100%' },
];

export const Onboarding = () => {
  const { user, updateUser } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0); // 0: Tunnel de Tri

  // ─── Iter. 411.hh — Path choice gate ───────────────────────────
  // Before the classic multi-step wizard we now show a 2-card branch:
  //   'accompanied' → keeps the existing 8-step guided onboarding
  //   'bypass'      → shortcut to a minimal essentials form (5 fields)
  //                   → hits /users/onboarding/bypass → routes to /workouts.
  // `null` = user hasn't picked yet (initial screen).
  const [pathMode, setPathMode] = useState(null);

  // Minimal essentials form (only used when pathMode === 'bypass')
  const [bypassData, setBypassData] = useState({
    weight: '',
    target_weight: '',
    height: '',
    height_feet: '',
    height_inches: '',
    age: '',
    sex: '',
    goal: 'maintain',
  });

  // Tunnel de Tri state
  const [tunnelData, setTunnelData] = useState({
    profile_group: '',
    mission: '',
    tech_level: '',
    dietary_approach: 'standard',
  });

  // Unit system state
  const [unitSystem, setUnitSystem] = useState(getDefaultUnitSystem(language));
  
  const [formData, setFormData] = useState({
    weight: '',
    target_weight: '',
    height: '',
    height_feet: '',
    height_inches: '',
    age: '',
    sex: '',
    activity_level: '',
    job_activity_level: 'desk',  // NEAT — corrects TDEE for physical jobs (Iter. 385)
    goal: 'maintain',
    language: language,
  });

  const [calculatedMacros, setCalculatedMacros] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showTransition, setShowTransition] = useState(false);
  const [deficitSpeed, setDeficitSpeed] = useState(null); // "0.5", "1.0", "1.5", "2.0"
  const [dietBriefingId, setDietBriefingId] = useState(null);

  // Health Profile state
  const [healthConditions, setHealthConditions] = useState([]);
  const [healthAllergies, setHealthAllergies] = useState([]);
  const [healthInjuries, setHealthInjuries] = useState([]);
  const [newInjuryText, setNewInjuryText] = useState('');
  const [commStyle, setCommStyle] = useState('commando');

  // 🌍 Cultural Quiz (Lot #11) — feeds Coach YUMIX system prompt with hyper-local food context
  const [culturalQuiz, setCulturalQuiz] = useState({
    staple_food: '',         // "Riz" | "Pain" | "Pommes de terre" | "Maïs" | "Manioc" | custom
    breakfast_typical: '',   // free text
    spices_sauces: [],       // string[] (chips + custom)
  });
  const [spiceInput, setSpiceInput] = useState('');

  // Auto-set sex and goal from tunnel
  const applyTunnelDefaults = () => {
    const updates = {};
    if (tunnelData.profile_group === 'femme') updates.sex = 'female';
    if (tunnelData.profile_group === 'homme') updates.sex = 'male';
    if (tunnelData.profile_group === 'senior') updates.age = '68';
    if (tunnelData.mission === 'perte_poids') updates.goal = 'lose';
    if (tunnelData.mission === 'performance') updates.goal = 'gain';
    if (tunnelData.mission === 'mobilite') updates.goal = 'maintain';
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Weight/Height helpers
  const getWeightInKg = () => {
    if (!formData.weight) return null;
    const value = parseFloat(formData.weight);
    return unitSystem === 'imperial' ? lbsToKg(value) : value;
  };

  const getHeightInCm = () => {
    if (unitSystem === 'imperial') {
      const feet = parseFloat(formData.height_feet) || 0;
      const inches = parseFloat(formData.height_inches) || 0;
      if (feet === 0) return null;
      return feetInchesToCm(feet, inches);
    }
    return parseFloat(formData.height) || null;
  };

  const calculateIMC = () => {
    const weightKg = getWeightInKg();
    const heightCm = getHeightInCm();
    if (!weightKg || !heightCm) return null;
    const heightM = heightCm / 100;
    return weightKg / (heightM * heightM);
  };

  const getIMCCategory = (imc) => {
    const categories = IMC_CATEGORIES[language] || IMC_CATEGORIES.fr;
    return categories.find(cat => imc <= cat.max) || categories[categories.length - 1];
  };

  const calculateMacros = (speedOverride) => {
    const weightKg = getWeightInKg();
    const heightCm = getHeightInCm();
    const age = parseInt(formData.age);
    const sex = formData.sex;
    const activityLevel = formData.activity_level;
    const goal = formData.goal;
    if (!weightKg || !heightCm || !age || !sex || !activityLevel) return null;

    const imc = calculateIMC();
    const imcCategory = getIMCCategory(imc);

    let bmr;
    if (sex === 'male') {
      bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * age) + 5;
    } else {
      bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * age) - 161;
    }

    const activities = ACTIVITY_LEVELS[language] || ACTIVITY_LEVELS.fr;
    const activity = activities.find(a => a.value === activityLevel);
    // 🛠️ NEAT job factor (Iter. 385) — chef de cuisine n'est PAS un employé de bureau.
    const jobProfiles = JOB_PROFILES[language] || JOB_PROFILES.fr;
    const jobProfile = jobProfiles.find(j => j.value === (formData.job_activity_level || 'desk'));
    const jobFactor = jobProfile?.factor || 1.0;
    const sportFactor = activity?.factor || 1.2;
    const tdee = Math.round(bmr * sportFactor * jobFactor * 0.90); // -10% correction

    // Use deficit speed if available (weight loss mode), otherwise use goal-based adjustment
    const speed = speedOverride || deficitSpeed;
    let calorieAdjust;
    if (goal === 'lose' && speed) {
      const speedDef = DEFICIT_SPEEDS.find(s => s.key === speed);
      calorieAdjust = speedDef ? -speedDef.deficit : -500;
    } else {
      const goals = GOALS[language] || GOALS.fr;
      const goalData = goals.find(g => g.value === goal);
      calorieAdjust = goalData?.calorieAdjust || 0;
    }

    let targetCalories = Math.round(tdee + calorieAdjust);
    let safetyApplied = false;
    if (targetCalories < 1200) {
      targetCalories = 1200;
      safetyApplied = true;
    }

    // Protein priority: higher in aggressive deficits AND for physical-job workers
    // (matches backend nutrition.py:calculate_deficit_macros — Iter. 385).
    const physicalJob = ['moderate', 'intense'].includes(formData.job_activity_level || 'desk');
    const speedVal = speed ? parseFloat(speed) : 0;
    let proteinPerKg = 1.8;
    if (goal === 'gain') proteinPerKg = 2.2;
    else if (goal === 'lose' && speedVal >= 1.5) proteinPerKg = physicalJob ? 2.2 : 2.0;
    else if (goal === 'lose' && physicalJob) proteinPerKg = 2.0;
    else if (goal === 'lose') proteinPerKg = 1.8;

    const targetWeightKg = formData.target_weight
      ? (unitSystem === 'imperial' ? lbsToKg(parseFloat(formData.target_weight)) : parseFloat(formData.target_weight))
      : weightKg;
    const proteinG = Math.round(targetWeightKg * proteinPerKg);
    const proteinCal = proteinG * 4;
    const fatCal = targetCalories * 0.28;
    const fatG = Math.round(fatCal / 9);
    const carbCal = targetCalories - proteinCal - fatCal;
    // Carbs floor — physical-job workers need glycogen to sustain shifts (Iter. 385)
    const carbsFloor = formData.job_activity_level === 'intense' ? 120
                     : formData.job_activity_level === 'moderate' ? 80
                     : 50;
    const carbG = Math.max(Math.round(carbCal / 4), carbsFloor);
    const fiberG = Math.round((targetCalories / 1000) * 14);

    return {
      imc: Math.round(imc * 10) / 10,
      imcCategory,
      bmr: Math.round(bmr),
      tdee,
      targetCalories,
      safetyApplied,
      protein_min_g: Math.round(proteinG * 0.9),
      protein_max_g: Math.round(proteinG * 1.1),
      protein_target_g: proteinG,
      carbs_target_g: carbG,
      fats_target_g: fatG,
      fiber_target_g: fiberG,
    };
  };

  const handleNext = () => {
    if (step === 0) {
      if (!tunnelData.profile_group || !tunnelData.mission || !tunnelData.tech_level) {
        toast.error(t('onboarding.select_three'));
        return;
      }
      applyTunnelDefaults();
      setStep(1);
    } else if (step === 1) {
      const hasWeight = formData.weight && parseFloat(formData.weight) > 0;
      const hasHeight = unitSystem === 'imperial' ? formData.height_feet : formData.height;
      if (!hasWeight) {
        toast.error(t('onboarding.err_weight_required', 'Le poids est obligatoire pour continuer'));
        return;
      }
      if (!hasHeight || !formData.age || !formData.sex) {
        toast.error(t('onboarding.err_all_required', 'Tous les champs sont requis'));
        return;
      }
      // ─── 18+ AGE GATE — YUMIX is reserved to adults ───
      const ageNum = parseInt(formData.age, 10);
      if (Number.isNaN(ageNum) || ageNum < 18) {
        toast.error(t('onboarding.err_age_18plus', 'YUMIX est réservée aux 18 ans et plus.'));
        return;
      }
      if (ageNum > 120) {
        toast.error(t('onboarding.err_age_invalid', 'Âge invalide.'));
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!formData.activity_level || !formData.goal) {
        toast.error(t('onboarding.err_pick_level_goal', 'Selectionne ton niveau et objectif'));
        return;
      }
      // If goal is weight loss, show deficit selector (step 3)
      if (formData.goal === 'lose') {
        setStep(3);
      } else {
        // Skip deficit step for maintain/gain — go to macros
        const macros = calculateMacros();
        setCalculatedMacros(macros);
        setStep(4);
      }
    } else if (step === 3) {
      // Deficit speed selected → calculate macros with deficit and go to macros step
      if (!deficitSpeed) {
        toast.error(t('onboarding.err_pick_loss_speed', 'Choisis ta vitesse de perte'));
        return;
      }
      const macros = calculateMacros(deficitSpeed);
      setCalculatedMacros(macros);
      setStep(4);
    } else if (step === 4) {
      setStep(5);
    } else if (step === 5) {
      // Health profile step — skip is OK (optional), go to comm style
      setStep(6);
    } else if (step === 6) {
      // Comm style → Cultural Quiz
      setStep(7);
    } else if (step === 7) {
      // Cultural Quiz → Profile (all fields optional; soft-skip allowed)
      setStep(8);
    }
  };

  const handleBack = () => {
    if (step === 4 && formData.goal !== 'lose') {
      setStep(2);
    } else if (step === 8) {
      setStep(7);
    } else if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    if (!calculatedMacros) return;
    if (!selectedProfile) {
      toast.error(t('onboarding.err_pick_combat_profile', 'Choisis ton profil de combat'));
      return;
    }
    setLoading(true);
    try {
      const weightKg = getWeightInKg();
      const heightCm = getHeightInCm();
      const targetWeightKg = formData.target_weight
        ? (unitSystem === 'imperial' ? lbsToKg(parseFloat(formData.target_weight)) : parseFloat(formData.target_weight))
        : weightKg;
      const weightLbs = kgToLbs(weightKg);
      const targetWeightLbs = kgToLbs(targetWeightKg);

      const payload = {
        current_weight_lbs: weightLbs,
        target_weight_lbs: targetWeightLbs,
        height_cm: heightCm,
        age: parseInt(formData.age),
        sex: formData.sex,
        activity_level: formData.activity_level,
        job_activity_level: formData.job_activity_level || 'desk',
        goal: formData.goal,
        language: formData.language,
        unit_system: unitSystem,
        active_profile: selectedProfile,
        profile_group: tunnelData.profile_group,
        mission: tunnelData.mission,
        tech_level: tunnelData.tech_level,
        dietary_approach: tunnelData.dietary_approach,
        dietary_preferences: [tunnelData.dietary_approach],
        imc: calculatedMacros.imc,
        daily_calories: calculatedMacros.targetCalories,
        protein_min_g: calculatedMacros.protein_min_g,
        protein_max_g: calculatedMacros.protein_max_g,
        carbs_target_g: calculatedMacros.carbs_target_g,
        fats_target_g: calculatedMacros.fats_target_g,
        fiber_target_g: calculatedMacros.fiber_target_g,
      };

      // Include deficit speed if user is in weight loss mode
      if (formData.goal === 'lose' && deficitSpeed) {
        payload.deficit_speed = deficitSpeed;
        payload.bmr = calculatedMacros.bmr;
        payload.tdee = calculatedMacros.tdee;
      }

      // Include health profile — Iter. 406.fr.59 : ALWAYS send (even empty) so
      // the Dashboard doesn't re-open the veterans catch-up modal for users
      // who legitimately have no conditions/allergies/injuries.
      payload.health_profile = {
        metabolic_conditions: healthConditions,
        allergies_intolerances: healthAllergies,
        injury_history: healthInjuries,
        completed_at: new Date().toISOString(),
      };

      // Communication style
      payload.communication_style = commStyle;

      // 🌍 Cultural profile (Lot #11) — drives Coach YUMIX prompt enrichment
      const hasCulturalData =
        culturalQuiz.staple_food.trim() ||
        culturalQuiz.breakfast_typical.trim() ||
        culturalQuiz.spices_sauces.length > 0;
      if (hasCulturalData) {
        payload.cultural_profile = {
          staple_food: culturalQuiz.staple_food.trim() || null,
          breakfast_typical: culturalQuiz.breakfast_typical.trim() || null,
          spices_sauces: culturalQuiz.spices_sauces.filter(s => s && s.trim()),
          navigator_lang: (typeof navigator !== 'undefined' && navigator.language) || null,
        };
      }

      const response = await api.post('/users/onboarding', payload);

      updateUser(response.data);
      changeLanguage(formData.language);

      try {
        await registerServiceWorker();
        await subscribeToPush();
      } catch (notifError) {
        // silent — push notifications are optional
      }
      setShowTransition(true);
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error(t('onboarding.err_create_profile', 'Erreur lors de la création du profil'));
      setLoading(false);
    }
  };

  const handleTransitionComplete = () => {
    setShowTransition(false);
    toast.success(t('onboarding.toast_profile_created', 'Profil créé avec succès!'));
    // Send freshly-onboarded users to the Deep Chat vocal first (Day 1 wow),
    // then the mandatory Day-7 Challenge intro, then the Dashboard.
    navigate('/deep-chat', { replace: true });
  };

  // ─── Iter. 411.hh — BYPASS submit ("Je connais le gym") ────────────
  // Minimal payload → /users/onboarding/bypass → straight to Coach YUMIX.
  // No transition modal, no deep-chat, no welcome challenge card.
  const handleBypassSubmit = async () => {
    // Frontend validation mirrors the Pydantic gates so users get a
    // helpful toast BEFORE the server round-trip.
    if (!bypassData.weight || !bypassData.target_weight || !bypassData.age || !bypassData.sex) {
      toast.error(t('onboarding.bypass_err_missing', 'Remplis les 5 champs essentiels'));
      return;
    }
    const weightKg = unitSystem === 'imperial'
      ? lbsToKg(parseFloat(bypassData.weight))
      : parseFloat(bypassData.weight);
    const targetKg = unitSystem === 'imperial'
      ? lbsToKg(parseFloat(bypassData.target_weight))
      : parseFloat(bypassData.target_weight);
    let heightCm;
    if (unitSystem === 'imperial') {
      const ft = parseFloat(bypassData.height_feet || 0);
      const inch = parseFloat(bypassData.height_inches || 0);
      if (!ft && !inch) {
        toast.error(t('onboarding.bypass_err_height', 'Entre ta taille'));
        return;
      }
      heightCm = feetInchesToCm(ft, inch);
    } else {
      heightCm = parseFloat(bypassData.height);
      if (!heightCm) {
        toast.error(t('onboarding.bypass_err_height', 'Entre ta taille'));
        return;
      }
    }

    setLoading(true);
    try {
      const response = await api.post('/users/onboarding/bypass', {
        current_weight_lbs: kgToLbs(weightKg),
        target_weight_lbs: kgToLbs(targetKg),
        height_cm: heightCm,
        age: parseInt(bypassData.age),
        sex: bypassData.sex,
        goal: bypassData.goal,
        language,
        unit_system: unitSystem,
      });
      updateUser(response.data);
      changeLanguage(language);
      toast.success(t('onboarding.bypass_success', 'Accès complet débloqué'));
      // Straight to Coach YUMIX plan generator — the whole point of bypass.
      navigate('/workouts?tab=plan', { replace: true });
    } catch (error) {
      console.error('Onboarding bypass error:', error);
      const detail = error?.response?.data?.detail || t('onboarding.err_create_profile', 'Erreur lors de la création du profil');
      toast.error(typeof detail === 'string' ? detail : 'Error');
      setLoading(false);
    }
  };

  const activities = ACTIVITY_LEVELS[language] || ACTIVITY_LEVELS.fr;
  const goals = GOALS[language] || GOALS.fr;
  const currentIMC = calculateIMC();
  const imcCategory = currentIMC ? getIMCCategory(currentIMC) : null;
  const progressPct = ((step + 1) / TOTAL_STEPS) * 100;

  const stepLabels = {
    0: t('onboarding.step_tunnel', 'Ton Tunnel'),
    1: t('onboarding.step_profile', 'Ton Profil'),
    2: t('onboarding.step_activity', 'Ton Activité'),
    3: t('onboarding.step_macros', 'Tes Macros'),
    4: t('onboarding.step_identity', 'Ton Identité'),
  };

  const stepIcons = {
    0: <Gauge className="w-7 h-7 text-primary-foreground" />,
    1: <User className="w-7 h-7 text-primary-foreground" />,
    2: <Activity className="w-7 h-7 text-primary-foreground" />,
    3: <Calculator className="w-7 h-7 text-primary-foreground" />,
    4: <Shield className="w-7 h-7 text-primary-foreground" />,
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="noise-overlay" />
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10" />

      <img
        src="/mascot-yuri.png"
        alt="Coach Yuri"
        className="absolute bottom-0 left-4 w-40 lg:w-56 h-auto object-contain opacity-80 hidden md:block drop-shadow-2xl"
      />

      {/* Iter. 411.hh — Path choice gate (shown FIRST) */}
      {pathMode === null && (
        <div className="w-full relative z-10">
          <OnboardingPathChoice
            language={language}
            t={t}
            onAccompanied={() => setPathMode('accompanied')}
            onBypass={() => setPathMode('bypass')}
          />
        </div>
      )}

      {/* Iter. 411.hh — Bypass essentials form */}
      {pathMode === 'bypass' && (
        <div className="w-full relative z-10">
          <OnboardingBypassForm
            language={language}
            t={t}
            unitSystem={unitSystem}
            setUnitSystem={setUnitSystem}
            bypassData={bypassData}
            setBypassData={setBypassData}
            loading={loading}
            onBack={() => setPathMode(null)}
            onSubmit={handleBypassSubmit}
          />
        </div>
      )}

      {/* Classic guided flow (Option A) — only when accompanied */}
      {pathMode === 'accompanied' && (
      <Card className="w-full max-w-lg bg-card border-border relative z-10" data-testid="onboarding-card">
        <CardHeader className="text-center pb-3">
          <div className="flex items-center justify-center gap-3 mb-3">
            <img src="/mascot-yuri.png" alt="Coach Yuri" className="w-14 h-14 object-contain md:hidden" />
            <div className="flex items-center justify-center w-12 h-12 bg-primary rounded hidden md:flex">
              {stepIcons[step]}
            </div>
          </div>
          <CardTitle className="font-heading text-3xl font-black uppercase tracking-tight">
            {stepLabels[step]}
          </CardTitle>
          <CardDescription>
            {t('onboarding.step_of', 'Étape {{current}} sur {{total}}', { current: step + 1, total: TOTAL_STEPS })}
          </CardDescription>
          <div className="mt-3">
            <Progress value={progressPct} className="h-2" />
          </div>
          {step > 0 && (
            <Link to="/coach-ai" className="mt-3 flex items-center justify-center gap-2 text-sm text-orange-500 hover:text-orange-400 transition-colors">
              <UserCircle className="w-4 h-4" />
              <span>{t('onboarding.talk_assistant', "Parler à l'Assistant YUMIX")}</span>
            </Link>
          )}
        </CardHeader>

        <CardContent>
          {/* ============ STEP 0: TUNNEL DE TRI ============ */}
          {step === 0 && (
            <div className="space-y-6" data-testid="tunnel-step">
              {/* Profile Group */}
              <div className="space-y-2">
                <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  {t('onboarding.i_am', 'Je suis...')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {PROFILE_GROUPS.map(pg => {
                    const sel = tunnelData.profile_group === pg.id;
                    return (
                      <button
                        key={pg.id}
                        type="button"
                        onClick={() => setTunnelData(prev => ({ ...prev, profile_group: pg.id }))}
                        data-testid={`tunnel-profile-${pg.id}`}
                        className={`p-4 rounded-xl text-center transition-all duration-200 border-2 ${
                          sel ? `${pg.border} bg-gradient-to-br ${pg.color}` : 'border-transparent bg-secondary/50 hover:border-muted-foreground/30'
                        }`}
                      >
                        <span className="text-3xl block mb-1">{pg.icon}</span>
                        <p className={`font-heading text-sm uppercase tracking-tight ${sel ? pg.text : 'text-foreground'}`}>
                          {t(`onboarding.${pg.i18nKey}`, pg.defaultFr)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Mission */}
              <div className="space-y-2">
                <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  {t('onboarding.my_mission', 'Ma mission...')}
                </label>
                <div className="space-y-2">
                  {MISSIONS.map(m => {
                    const sel = tunnelData.mission === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setTunnelData(prev => ({ ...prev, mission: m.id }))}
                        data-testid={`tunnel-mission-${m.id}`}
                        className={`w-full p-4 rounded-xl text-left transition-all duration-200 border-2 flex items-center gap-4 ${
                          sel ? `${m.border} bg-gradient-to-r ${m.color}` : 'border-transparent bg-secondary/50 hover:border-muted-foreground/30'
                        }`}
                      >
                        <span className="text-2xl">{m.icon}</span>
                        <div>
                          <p className={`font-heading text-sm uppercase tracking-tight ${sel ? m.text : 'text-foreground'}`}>
                            {t(`onboarding.${m.i18nKey}`, m.defaultFr)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t(`onboarding.${m.descKey}`, m.defaultDescFr)}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tech Level */}
              <div className="space-y-2">
                <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  {t('onboarding.my_tech_level', 'Mon niveau techno...')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {TECH_LEVELS.map(tl => {
                    const sel = tunnelData.tech_level === tl.id;
                    return (
                      <button
                        key={tl.id}
                        type="button"
                        onClick={() => setTunnelData(prev => ({ ...prev, tech_level: tl.id }))}
                        data-testid={`tunnel-tech-${tl.id}`}
                        className={`p-4 rounded-xl text-center transition-all duration-200 border-2 ${
                          sel ? `${tl.border} bg-gradient-to-br ${tl.color}` : 'border-transparent bg-secondary/50 hover:border-muted-foreground/30'
                        }`}
                      >
                        <span className="text-2xl block mb-1">{tl.icon}</span>
                        <p className={`font-heading text-sm uppercase tracking-tight ${sel ? tl.text : 'text-foreground'}`}>
                          {t(`onboarding.${tl.i18nKey}`, tl.defaultFr)}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {t(`onboarding.${tl.descKey}`, tl.defaultDescFr)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Dietary Approach */}
              <div className="space-y-2">
                <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  {t('onboarding.my_dietary_approach', 'Mon approche alimentaire...')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {DIETARY_APPROACHES.map(da => {
                    const sel = tunnelData.dietary_approach === da.id;
                    return (
                      <button
                        key={da.id}
                        type="button"
                        onClick={() => setDietBriefingId(da.id)}
                        data-testid={`tunnel-diet-${da.id}`}
                        className={`p-3 rounded-xl text-center transition-all duration-200 border-2 ${
                          sel ? `${da.border} bg-gradient-to-br ${da.color}` : 'border-transparent bg-secondary/50 hover:border-muted-foreground/30'
                        }`}
                      >
                        <p className={`font-heading text-xs uppercase tracking-tight ${sel ? da.text : 'text-foreground'}`}>
                          {t(`onboarding.${da.i18nKey}`, da.defaultFr)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ============ STEP 1: BASIC INFO ============ */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('onboarding.language', 'Langue')}</Label>
                  <Select value={formData.language} onValueChange={(v) => setFormData({ ...formData, language: v })}>
                    <SelectTrigger className="bg-secondary border-input"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">Francais</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Globe className="w-4 h-4 text-primary" />{t('onboarding.units', 'Unites')}</Label>
                  <Select value={unitSystem} onValueChange={(v) => { setUnitSystem(v); setFormData({ ...formData, weight: '', height: '', height_feet: '', height_inches: '' }); }}>
                    <SelectTrigger className="bg-secondary border-input"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="metric">{UNIT_SYSTEMS.metric.label[language]}</SelectItem>
                      <SelectItem value="imperial">{UNIT_SYSTEMS.imperial.label[language]}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Scale className="w-4 h-4 text-primary" />{t('onboarding.weight_label', 'Poids')} ({unitSystem === 'imperial' ? 'lbs' : 'kg'})</Label>
                  <Input type="number" placeholder={unitSystem === 'imperial' ? '154' : '70'} value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })} className="bg-secondary border-input" />
                </div>
                {unitSystem === 'metric' ? (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Ruler className="w-4 h-4 text-primary" />{t('onboarding.height_cm', 'Taille (cm)')}</Label>
                    <Input type="number" placeholder="175" value={formData.height} onChange={(e) => setFormData({ ...formData, height: e.target.value })} className="bg-secondary border-input" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Ruler className="w-4 h-4 text-primary" />{t('onboarding.height_label', 'Taille')}</Label>
                    <div className="flex gap-2">
                      <Input type="number" placeholder={t('onboarding.unit_ft', 'pi')} value={formData.height_feet} onChange={(e) => setFormData({ ...formData, height_feet: e.target.value })} className="bg-secondary border-input w-1/2" />
                      <Input type="number" placeholder={t('onboarding.unit_in', 'po')} value={formData.height_inches} onChange={(e) => setFormData({ ...formData, height_inches: e.target.value })} className="bg-secondary border-input w-1/2" />
                    </div>
                  </div>
                )}
              </div>
              {currentIMC && imcCategory && (
                <div className={`p-4 rounded-xl ${imcCategory.bg} border border-border`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('onboarding.your_bmi', 'Ton IMC')}</span>
                    <span className={`font-heading text-2xl font-bold ${imcCategory.color}`}>{Math.round(currentIMC * 10) / 10}</span>
                  </div>
                  <p className={`text-sm ${imcCategory.color}`}>{imcCategory.label}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><User className="w-4 h-4 text-primary" />{t('onboarding.age_label', 'Age')}</Label>
                  <Input type="number" placeholder="30" value={formData.age} onChange={(e) => setFormData({ ...formData, age: e.target.value })} className="bg-secondary border-input" />
                </div>
                <div className="space-y-2">
                  <Label>{t('onboarding.sex_label', 'Sexe')}</Label>
                  <Select value={formData.sex} onValueChange={(v) => setFormData({ ...formData, sex: v })}>
                    <SelectTrigger className="bg-secondary border-input"><SelectValue placeholder={t('onboarding.select_placeholder', 'Selectionner')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">{t('onboarding.sex_male', 'Homme')}</SelectItem>
                      <SelectItem value="female">{t('onboarding.sex_female', 'Femme')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Target className="w-4 h-4 text-primary" />{t('onboarding.target_weight_optional', 'Poids cible ({{unit}}) - optionnel', { unit: unitSystem === 'imperial' ? 'lbs' : 'kg' })}</Label>
                <Input type="number" placeholder={formData.weight || (unitSystem === 'imperial' ? '154' : '70')} value={formData.target_weight} onChange={(e) => setFormData({ ...formData, target_weight: e.target.value })} className="bg-secondary border-input" />
              </div>
            </div>
          )}

          {/* ============ STEP 2: ACTIVITY & GOAL ============ */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="space-y-3">
                <Label className="flex items-center gap-2 text-lg"><Activity className="w-5 h-5 text-primary" />{t('onboarding.activity_level', "Niveau d'activité")}</Label>
                <div className="space-y-2">
                  {activities.map((activity) => (
                    <button key={activity.value} type="button" onClick={() => setFormData({ ...formData, activity_level: activity.value })}
                      className={`w-full p-4 rounded-xl text-left transition-all ${formData.activity_level === activity.value ? 'bg-primary/20 border-2 border-primary' : 'bg-secondary/50 border-2 border-transparent hover:border-primary/30'}`}>
                      <div className="flex items-center justify-between">
                        <div><p className="font-medium text-foreground">{activity.label}</p><p className="text-sm text-muted-foreground">{activity.desc}</p></div>
                        <span className="text-xs bg-background px-2 py-1 rounded text-muted-foreground">x{activity.factor}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* 🛠️ JOB ACTIVITY — NEAT factor (Iter. 385) */}
              <div className="space-y-3" data-testid="onboarding-job-profile-section">
                <div>
                  <Label className="flex items-center gap-2 text-lg">
                    <Briefcase className="w-5 h-5 text-orange-400" />
                    {t('onboarding.job_profile', 'Profil professionnel')}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('onboarding.job_profile_hint', 'Une journée debout en cuisine ≠ une journée au bureau. On corrige ton budget.')}
                  </p>
                </div>
                <div className="space-y-2">
                  {(JOB_PROFILES[language] || JOB_PROFILES.fr).map((job) => (
                    <button
                      key={job.value}
                      type="button"
                      data-testid={`onboarding-job-profile-${job.value}`}
                      onClick={() => setFormData({ ...formData, job_activity_level: job.value })}
                      className={`w-full p-3 rounded-xl text-left transition-all ${
                        formData.job_activity_level === job.value
                          ? 'bg-orange-500/15 border-2 border-orange-500'
                          : 'bg-secondary/50 border-2 border-transparent hover:border-orange-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-foreground">{job.label}</p>
                          <p className="text-xs text-muted-foreground">{job.desc}</p>
                        </div>
                        <span className="text-xs bg-background px-2 py-1 rounded text-orange-300 shrink-0">×{job.factor}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label className="flex items-center gap-2 text-lg"><Target className="w-5 h-5 text-primary" />{t('onboarding.your_goal', 'Ton objectif')}</Label>
                <div className="grid grid-cols-3 gap-2">
                  {goals.map((goal) => (
                    <button key={goal.value} type="button" onClick={() => setFormData({ ...formData, goal: goal.value })}
                      className={`p-3 rounded-xl text-center transition-all ${formData.goal === goal.value ? 'bg-primary/20 border-2 border-primary' : 'bg-secondary/50 border-2 border-transparent hover:border-primary/30'}`}>
                      <p className="text-sm font-medium text-foreground">{goal.label}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ============ STEP 3: DEFICIT SPEED SELECTOR (weight loss only) ============ */}
          {step === 3 && (
            <div className="space-y-5" data-testid="onboarding-deficit-step">
              <div className="text-center mb-2">
                <Target className="w-8 h-8 text-primary mx-auto mb-2" />
                <h3 className="font-heading text-lg uppercase tracking-tight text-foreground">
                  {t('onboarding.battle_plan', 'Ton plan de guerre')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('onboarding.battle_plan_subtitle', 'Choisis ta vitesse de perte de poids hebdomadaire.')}
                </p>
              </div>

              {/* Difficulty gradient bar */}
              <div className="relative h-2 rounded-full overflow-hidden bg-secondary">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-yellow-500 via-orange-500 to-red-600 rounded-full" />
              </div>

              <div className="space-y-2">
                {DEFICIT_SPEEDS.map((opt) => {
                  const isSelected = deficitSpeed === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setDeficitSpeed(opt.key)}
                      data-testid={`onboarding-deficit-${opt.key}`}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                        isSelected
                          ? `${opt.bg} ${opt.border} scale-[1.01]`
                          : 'bg-secondary/40 border-transparent hover:border-muted-foreground/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-heading text-base uppercase tracking-tight ${isSelected ? opt.text : 'text-foreground'}`}>
                              {t(`onboarding.${opt.labelKey}`, opt.defaultLabelFr)}
                            </span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isSelected ? opt.bg + ' ' + opt.text : 'bg-secondary text-muted-foreground'}`}>
                              -{opt.weekly}/{t('onboarding.unit_week', 'sem')}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {t(`onboarding.${opt.descKey}`, opt.defaultDescFr)}
                          </p>
                          {/* Difficulty bar per option */}
                          <div className="mt-2 h-1.5 rounded-full bg-secondary/50 overflow-hidden w-full max-w-[200px]">
                            <div className={`h-full rounded-full bg-gradient-to-r ${opt.color}`} style={{ width: opt.barWidth }} />
                          </div>
                        </div>
                        <div className="text-right ml-3">
                          <p className={`font-heading text-lg font-bold ${isSelected ? opt.text : 'text-muted-foreground'}`}>-{opt.deficit}</p>
                          <p className="text-[10px] text-muted-foreground">kcal/{t('onboarding.unit_day', 'jour')}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Safety warning for Commando */}
              {deficitSpeed === '2.0' && (
                <div className="flex items-start gap-2.5 p-3 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="onboarding-deficit-warning">
                  <Scale className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    {t('onboarding.commando_warning', 'Mode Commando: déficit agressif reserve aux objectifs a court terme. Si ton apport tombe sous 1200 kcal, nous ajusterons automatiquement pour ta securite.')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ============ STEP 4: MACRO RESULTS ============ */}
          {step === 4 && calculatedMacros && (
            <div className="space-y-6">
              <div className={`p-4 rounded-xl ${calculatedMacros.imcCategory.bg}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">{t('onboarding.your_bmi', 'Ton IMC')}</span>
                  <span className={`font-heading text-3xl font-bold ${calculatedMacros.imcCategory.color}`}>{calculatedMacros.imc}</span>
                </div>
                <p className={`font-medium ${calculatedMacros.imcCategory.color}`}>{calculatedMacros.imcCategory.label}</p>
              </div>
              <div className="bg-primary/20 p-4 rounded-xl border border-primary/30 text-center">
                <Flame className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">{t('onboarding.daily_calorie_budget', 'Ton budget calorique quotidien')}</p>
                <p className="font-heading text-4xl text-primary">{calculatedMacros.targetCalories}</p>
                <p className="text-sm text-muted-foreground">calories</p>
              </div>
              <div
                className="rounded-xl p-4 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(168,85,247,0.05), rgba(15,3,30,0.6))',
                  border: '1px solid rgba(168,85,247,0.25)',
                  boxShadow: '0 0 22px rgba(168,85,247,0.15)',
                }}
              >
                <h4 className="font-heading text-sm uppercase text-muted-foreground mb-4 text-center tracking-wider">{t('onboarding.recommended_macros', 'Tes macros recommandes')}</h4>
                <div className="grid grid-cols-2 gap-3">
                  <StatTile theme="blue"    value={`${calculatedMacros.protein_target_g}g`} label={t('meal.protein', 'Protéines')} />
                  <StatTile theme="amber"   value={`${calculatedMacros.carbs_target_g}g`}   label={t('meal.carbs', 'Glucides')}   />
                  <StatTile theme="orange"  value={`${calculatedMacros.fats_target_g}g`}    label={t('meal.fats', 'Lipides')}     />
                  <StatTile theme="emerald" value={`${calculatedMacros.fiber_target_g}g`}   label={t('meal.fiber', 'Fibres')}     />
                </div>
              </div>
              <p className="text-xs text-center text-muted-foreground">{t('onboarding.adjust_settings_hint', 'Tu pourras ajuster ces valeurs dans les parametres.')}</p>
              {/* Safety floor warning */}
              {calculatedMacros.safetyApplied && (
                <div className="flex items-start gap-2.5 p-3 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="onboarding-safety-warning">
                  <Scale className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-400 font-medium">
                    {t('onboarding.safety_warning_floor', 'Attention, ce déficit est trop bas pour ta santé. Nous avons ajuste ton plan a 1200 kcal minimum pour ta securite.')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ============ STEP 5: HEALTH PROFILE ============ */}
          {step === 5 && (
            <div className="space-y-5" data-testid="onboarding-health-step">
              <div className="text-center space-y-1">
                <HeartPulse className="w-8 h-8 mx-auto text-red-400" />
                <p className="text-sm text-muted-foreground">
                  {t('onboarding.health_intro', 'Aide ton Coach YUMIX a te proteger. Selectionne ce qui te concerne (optionnel).')}
                </p>
              </div>

              {/* Metabolic Conditions */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground uppercase tracking-wide">
                  {t('onboarding.metabolic_conditions', 'Conditions Metaboliques')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {METABOLIC_CONDITIONS.map(opt => {
                    const isActive = healthConditions.includes(opt.id);
                    return (
                      <button key={opt.id} type="button"
                        onClick={() => setHealthConditions(prev => prev.includes(opt.id) ? prev.filter(x => x !== opt.id) : [...prev, opt.id])}
                        data-testid={`onb-condition-${opt.id}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
                          isActive ? 'bg-primary/20 border-primary text-primary' : 'bg-secondary/50 border-border text-muted-foreground hover:border-primary/40'
                        }`}>
                        <span className="mr-1">{opt.icon}</span>
                        {t(`onboarding.cond_${opt.id}`, opt.fr)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Allergies */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground uppercase tracking-wide">
                  {t('onboarding.allergies_intolerances', 'Allergies / Intolerances')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {ALLERGIES_OPTIONS.map(opt => {
                    const isActive = healthAllergies.includes(opt.id);
                    return (
                      <button key={opt.id} type="button"
                        onClick={() => setHealthAllergies(prev => prev.includes(opt.id) ? prev.filter(x => x !== opt.id) : [...prev, opt.id])}
                        data-testid={`onb-allergy-${opt.id}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
                          isActive ? 'bg-orange-500/20 border-orange-500 text-orange-400' : 'bg-secondary/50 border-border text-muted-foreground hover:border-orange-500/40'
                        }`}>
                        <span className="mr-1">{opt.icon}</span>
                        {t(`onboarding.allergy_${opt.id}`, opt.fr)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Injuries */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground uppercase tracking-wide">
                  {t('onboarding.injuries_weak_spots', 'Blessures / Zones Fragiles')}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={newInjuryText}
                    onChange={(e) => setNewInjuryText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const t = newInjuryText.trim();
                        if (t && !healthInjuries.includes(t)) {
                          setHealthInjuries(prev => [...prev, t]);
                          setNewInjuryText('');
                        }
                      }
                    }}
                    placeholder={t('onboarding.injury_placeholder', 'Ex: Genou droit fragile')}
                    className="bg-secondary border-border text-sm flex-1"
                    data-testid="onb-injury-input"
                  />
                  <Button type="button" size="sm"
                    onClick={() => {
                      const t = newInjuryText.trim();
                      if (t && !healthInjuries.includes(t)) {
                        setHealthInjuries(prev => [...prev, t]);
                        setNewInjuryText('');
                      }
                    }}
                    disabled={!newInjuryText.trim()}
                    className="px-3"
                    data-testid="onb-injury-add-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {healthInjuries.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {healthInjuries.map((inj, idx) => (
                      <div key={idx} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-purple-500/15 border border-purple-500/30 text-purple-300">
                        <span>{inj}</span>
                        <button type="button" onClick={() => setHealthInjuries(prev => prev.filter((_, i) => i !== idx))} className="hover:text-red-400">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {(healthConditions.length > 0 || healthAllergies.length > 0 || healthInjuries.length > 0) && (
                <div className="p-2.5 rounded-lg bg-primary/10 border border-primary/20">
                  <p className="text-[11px] text-primary">
                    {t('onboarding.health_adapt_note', 'Le Coach YUMIX adaptera automatiquement ses recommandations a ta realite medicale.')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ============ STEP 6: COMMUNICATION STYLE ============ */}
          {step === 6 && (
            <div className="space-y-4" data-testid="onboarding-comm-style-step">
              <div className="text-center space-y-1 mb-2">
                <p className="font-heading text-sm uppercase tracking-wide text-primary">
                  {t('onboarding.how_coach_talks', 'Comment veux-tu que ton Coach te parle ?')}
                </p>
              </div>
              <div className="space-y-2.5">
                {COMMUNICATION_STYLES.map(style => {
                  const isActive = commStyle === style.id;
                  return (
                    <button
                      key={style.id}
                      type="button"
                      onClick={() => setCommStyle(style.id)}
                      data-testid={`onb-comm-${style.id}`}
                      className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${
                        isActive ? style.activeColor : style.color + ' hover:brightness-110'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 mb-1">
                        <span className="text-xl">{style.emoji}</span>
                        <div>
                          <p className="font-heading text-xs uppercase tracking-tight text-foreground">
                            {t(`onboarding.comm_${style.id}_title`, style.title_fr)}
                          </p>
                          <p className="text-[9px] text-muted-foreground uppercase tracking-wide">
                            {t(`onboarding.comm_${style.id}_subtitle`, style.subtitle_fr)}
                          </p>
                        </div>
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-relaxed pl-9">
                        {t(`onboarding.comm_${style.id}_desc`, style.desc_fr)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ============ STEP 7: CULTURAL QUIZ (Lot #11) ============ */}
          {step === 7 && (
            <div className="space-y-5" data-testid="onboarding-cultural-step">
              <div className="text-center space-y-1">
                <p className="font-heading text-base uppercase tracking-tight text-primary">
                  {t('onboarding.cultural_title', 'Cuisine de chez toi')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('onboarding.cultural_subtitle', 'Aide ton Chef à proposer des recettes qui te parlent. 30 secondes, tout est facultatif.')}
                </p>
              </div>

              {/* Q1 — Staple food */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center">1</span>
                  {t('onboarding.cq_q1', 'Quel est l\'accompagnement / féculent de base le plus consommé dans ta région ?')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { id: 'riz',       k: 'cq_staple_rice',     d: 'Riz' },
                    { id: 'pain',      k: 'cq_staple_bread',    d: 'Pain' },
                    { id: 'pdt',       k: 'cq_staple_potatoes', d: 'Pommes de terre' },
                    { id: 'mais',      k: 'cq_staple_corn',     d: 'Maïs' },
                    { id: 'manioc',    k: 'cq_staple_cassava',  d: 'Manioc' },
                    { id: 'pates',     k: 'cq_staple_pasta',    d: 'Pâtes' },
                    { id: 'quinoa',    k: 'cq_staple_quinoa',   d: 'Quinoa' },
                  ].map(opt => {
                    const label = t(`onboarding.${opt.k}`, opt.d);
                    const isActive = culturalQuiz.staple_food === label;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        data-testid={`cq-staple-${opt.id}`}
                        onClick={() => setCulturalQuiz(q => ({ ...q, staple_food: isActive ? '' : label }))}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
                          isActive
                            ? 'bg-primary/20 border-primary text-primary'
                            : 'bg-secondary/50 border-border text-muted-foreground hover:border-primary/40'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <Input
                  type="text"
                  value={culturalQuiz.staple_food}
                  onChange={(e) => setCulturalQuiz(q => ({ ...q, staple_food: e.target.value }))}
                  placeholder={t('onboarding.cq_staple_placeholder', 'Autre… (écris ta réponse)')}
                  className="bg-secondary border-input text-sm"
                  data-testid="cq-staple-input"
                  maxLength={80}
                />
              </div>

              {/* Q2 — Breakfast */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center">2</span>
                  {t('onboarding.cq_q2', 'À quoi ressemble ton petit-déjeuner (premier repas) typique ?')}
                </label>
                <textarea
                  value={culturalQuiz.breakfast_typical}
                  onChange={(e) => setCulturalQuiz(q => ({ ...q, breakfast_typical: e.target.value }))}
                  placeholder={t('onboarding.cq_breakfast_placeholder', 'Ex : café + 2 tranches de pain avec beurre, ou bol de yaourt avec fruits + granola…')}
                  rows={2}
                  maxLength={400}
                  className="w-full px-3 py-2 rounded-md bg-secondary border border-input text-sm text-foreground placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:ring-2 focus:ring-primary/40"
                  data-testid="cq-breakfast-input"
                />
              </div>

              {/* Q3 — Spices / herbs / sauces */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center">3</span>
                  {t('onboarding.cq_q3', 'Quelles épices, herbes ou sauces sont incontournables dans ta cuisine locale ?')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { id: 'ail',       k: 'cq_spice_garlic',    d: 'Ail' },
                    { id: 'oignon',    k: 'cq_spice_onion',     d: 'Oignon' },
                    { id: 'paprika',   k: 'cq_spice_paprika',   d: 'Paprika' },
                    { id: 'cumin',     k: 'cq_spice_cumin',     d: 'Cumin' },
                    { id: 'curcuma',   k: 'cq_spice_turmeric',  d: 'Curcuma' },
                    { id: 'gingembre', k: 'cq_spice_ginger',    d: 'Gingembre' },
                    { id: 'piment',    k: 'cq_spice_chili',     d: 'Piment' },
                    { id: 'basilic',   k: 'cq_spice_basil',     d: 'Basilic' },
                    { id: 'thym',      k: 'cq_spice_thyme',     d: 'Thym' },
                    { id: 'coriandre', k: 'cq_spice_cilantro',  d: 'Coriandre' },
                    { id: 'soja',      k: 'cq_spice_soy',       d: 'Sauce soja' },
                    { id: 'sriracha',  k: 'cq_spice_sriracha',  d: 'Sriracha' },
                  ].map(opt => {
                    const label = t(`onboarding.${opt.k}`, opt.d);
                    const isActive = culturalQuiz.spices_sauces.includes(label);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        data-testid={`cq-spice-${opt.id}`}
                        onClick={() => setCulturalQuiz(q => ({
                          ...q,
                          spices_sauces: isActive
                            ? q.spices_sauces.filter(s => s !== label)
                            : [...q.spices_sauces, label],
                        }))}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
                          isActive
                            ? 'bg-primary/20 border-primary text-primary'
                            : 'bg-secondary/50 border-border text-muted-foreground hover:border-primary/40'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={spiceInput}
                    onChange={(e) => setSpiceInput(e.target.value)}
                    placeholder={t('onboarding.cq_spice_placeholder', 'Ajouter (ex : harissa, sambal oelek, ras el hanout…)')}
                    className="bg-secondary border-input text-sm flex-1"
                    data-testid="cq-spice-input"
                    maxLength={60}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && spiceInput.trim()) {
                        e.preventDefault();
                        const v = spiceInput.trim();
                        if (!culturalQuiz.spices_sauces.includes(v)) {
                          setCulturalQuiz(q => ({ ...q, spices_sauces: [...q.spices_sauces, v] }));
                        }
                        setSpiceInput('');
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const v = spiceInput.trim();
                      if (v && !culturalQuiz.spices_sauces.includes(v)) {
                        setCulturalQuiz(q => ({ ...q, spices_sauces: [...q.spices_sauces, v] }));
                      }
                      setSpiceInput('');
                    }}
                    disabled={!spiceInput.trim()}
                    data-testid="cq-spice-add-btn"
                  >
                    +
                  </Button>
                </div>
                {culturalQuiz.spices_sauces.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="cq-spice-chips">
                    {culturalQuiz.spices_sauces.map((s) => (
                      <span
                        key={s}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/15 border border-primary/30 text-primary"
                      >
                        {s}
                        <button
                          type="button"
                          onClick={() => setCulturalQuiz(q => ({ ...q, spices_sauces: q.spices_sauces.filter(x => x !== s) }))}
                          className="hover:text-foreground"
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-[10px] text-muted-foreground text-center italic">
                {t('onboarding.cq_chef_note', '👨‍🍳 Le Chef YUMIX utilisera ces infos pour adapter chaque recette à ta cuisine locale.')}
              </p>
            </div>
          )}

          {/* ============ STEP 8: PROFILE SELECTION ============ */}
          {step === 8 && (
            <div className="space-y-5" data-testid="onboarding-profile-step">
              <p className="text-sm text-muted-foreground text-center">
                {t('onboarding.choose_profile_phase', 'Choisis le profil qui correspond a ta phase de vie.')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {Object.values(PROFILE_DEFINITIONS).map((p) => {
                  const icons = { Target, Crown, Shield, Flame };
                  const Icon = icons[p.icon] || Target;
                  const isSelected = selectedProfile === p.id;
                  return (
                    <button key={p.id} type="button" onClick={() => setSelectedProfile(p.id)} data-testid={`profile-option-${p.id}`}
                      className={`relative p-4 rounded-xl text-center transition-all duration-300 ${isSelected ? 'border-2 scale-[1.02]' : 'border-2 border-transparent bg-secondary/50 hover:border-muted-foreground/30'}`}
                      style={isSelected ? { borderColor: p.colors.primary, background: `linear-gradient(135deg, ${p.colors.primary}15, ${p.colors.accent}15)`, boxShadow: `0 0 20px ${p.colors.primary}20` } : undefined}>
                      <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2"
                        style={{ background: isSelected ? `linear-gradient(135deg, ${p.colors.primary}, ${p.colors.accent})` : undefined }}>
                        <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-muted-foreground'}`} />
                      </div>
                      <p className="font-heading text-base uppercase tracking-tight text-foreground">{p.label}</p>
                      <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{t(`onboarding.profile_${p.id}_desc`, p.description_fr)}</p>
                    </button>
                  );
                })}
              </div>
              {selectedProfile && (
                <div className="p-4 rounded-xl border-2 text-center"
                  style={{ borderColor: PROFILE_DEFINITIONS[selectedProfile]?.colors.primary + '60', background: `linear-gradient(135deg, ${PROFILE_DEFINITIONS[selectedProfile]?.colors.primary}08, ${PROFILE_DEFINITIONS[selectedProfile]?.colors.accent}08)` }}
                  data-testid="profile-serment">
                  <p className="font-heading text-sm uppercase tracking-wider text-muted-foreground mb-2">{t('onboarding.oath', 'Serment')}</p>
                  <p className="text-sm text-foreground leading-relaxed italic">
                    {t('onboarding.oath_text', "\"Je m'engage en tant que {{label}}. Mon coach adapte ma strategie a cette identite.\"", { label: PROFILE_DEFINITIONS[selectedProfile]?.label })}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-6">
            {step > 0 && (
              <Button type="button" variant="outline" onClick={handleBack} className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" />{t('common.back', 'Retour')}
              </Button>
            )}
            {step < 8 ? (
              <Button type="button" onClick={handleNext} className="flex-1 bg-primary text-primary-foreground" data-testid="onboarding-next-btn">
                {step === 0
                  ? t('onboarding.btn_start', 'Commencer')
                  : t('common.next', 'Suivant')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmit} disabled={loading || !selectedProfile}
                className="flex-1 bg-primary text-primary-foreground font-heading uppercase" data-testid="onboarding-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Zap className="w-5 h-5 mr-2" />}
                {t('onboarding.btn_activate_profile', 'Activer mon profil')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      )}
      {/* end Iter. 411.hh — pathMode === 'accompanied' block */}

      <ProfileTransition profile={selectedProfile} visible={showTransition} onComplete={handleTransitionComplete} />

      {/* Dietary Briefing Modal */}
      {dietBriefingId && (
        <DietaryBriefingModal
          approachId={dietBriefingId}
          language={language}
          onConfirm={() => {
            setTunnelData(prev => ({ ...prev, dietary_approach: dietBriefingId }));
            setDietBriefingId(null);
          }}
          onCancel={() => setDietBriefingId(null)}
        />
      )}
    </div>
  );
};

export default Onboarding;
